import { gql } from 'graphql-tag'

import {
  LIVE_HELP_REQUEST_FRAGMENT,
  NOTIFICATION_FRAGMENT,
  POST_EVENT_FRAGMENT,
  POST_FEEDBACK_FRAGMENT,
  POST_FRAGMENT,
  POST_REACTION_FRAGMENT,
  SESSION_FRAGMENT,
  SESSION_REGISTRATION_FRAGMENT,
  USER_FRAGMENT,
  USER_ONLINE_FRAGMENT,
  WORLD_FRAGMENT,
  WORLD_MEMBER_FRAGMENT,
} from './fragments'

export const SESSION_JOIN_INFO_SUBSCRIPTION = gql`
  subscription SessionJoinInfo($id: Int!) {
    session_by_pk(id: $id) {
      start_date
      end_date
      attempted_hosting
      zoom_end_date
    }
  }
`

export const POST_FEEDBACKS_SUBSCRIPTION = gql`
  subscription PostFeedback($id: Int!) {
    post_by_pk(id: $id) {
      id
      post_feedbacks(order_by: { created_at: asc }) {
        ...PostFeedback
      }
    }
  }

  ${POST_FEEDBACK_FRAGMENT}
`

export const USER_UNDISPLAYED_ACHIEVEMENTS_SUBSCRIPTION = gql`
  subscription UserUndisplayedAchievements($userId: Int!) {
    user_by_pk(id: $userId) {
      id
      undisplayed_achievements
    }
  }
`

export const ONLINE_USERS_QUERY = gql`
  fragment UserOnlineBase on user_online {
    id
    last_online_at
  }

  query OnlineUsers {
    user_online {
      ...UserOnlineBase
    }
  }
`

export const ALL_MEMBER_WORLD_STATS_SUBSCRIPTION = gql`
  subscription AllWorldStats(
    $currentUserId: Int!
    $allowedLevels: [String!]!
    $allowedChannelTypes: [String!]!
    $allowedPostStatuses: [String!]!
  ) {
    world_member(where: { user_id: { _eq: $currentUserId } }) {
      level
      world {
        id
        posts_aggregate(
          where: {
            status: { _in: $allowedPostStatuses }
            channel: { type: { _in: $allowedChannelTypes } }
          }
        ) {
          aggregate {
            count
          }
        }
        world_members_aggregate(
          where: { user_online: { id: { _is_null: false } }, level: { _in: $allowedLevels } }
        ) {
          aggregate {
            count
          }
        }
      }
    }
  }

  ${WORLD_MEMBER_FRAGMENT}
  ${USER_ONLINE_FRAGMENT}
`

export const ONLINE_USERS_IN_WORLD_QUERY = gql`
  query OnlineUsersInWorld($id: Int!, $allowedLevels: [String!]!, $limit: Int) {
    world_by_pk(id: $id) {
      world_members_aggregate(
        where: { user_online: { id: { _is_null: false } }, level: { _in: $allowedLevels } }
      ) {
        aggregate {
          count
        }
      }
      world_members(
        where: { user_online: { id: { _is_null: false } }, level: { _in: $allowedLevels } }
        limit: $limit
      ) {
        ...WorldMember
        user_online {
          ...UserOnline
        }
      }
    }
  }

  ${WORLD_MEMBER_FRAGMENT}
  ${USER_ONLINE_FRAGMENT}
`

export const ONLINE_USERS_IN_CHANNEL_SUBSCRIPTION = gql`
  subscription OnlineUsersInChannel($id: Int!, $limit: Int) {
    channel_by_pk(id: $id) {
      world_id
      channel_members_aggregate(where: { user_online: { id: { _is_null: false } } }) {
        aggregate {
          count
        }
      }
      channel_members(where: { user_online: { id: { _is_null: false } } }, limit: $limit) {
        user_online {
          ...UserOnline
          world_members {
            ...WorldMember
          }
        }
      }
    }
  }

  ${WORLD_MEMBER_FRAGMENT}
  ${USER_ONLINE_FRAGMENT}
`

const LIVE_HELP_REQUEST_WITH_SESSION_FRAGMENT = gql`
  fragment LiveHelpRequestWithSession on live_help_request {
    ...LiveHelpRequest
    session {
      ...Session
      world {
        ...World
      }
      session_registrations(where: { user_id: { _eq: $sessionRegistrationUserId } }) {
        ...SessionRegistration
      }
    }
  }

  ${LIVE_HELP_REQUEST_FRAGMENT}
  ${USER_FRAGMENT}
  ${SESSION_FRAGMENT}
  ${WORLD_FRAGMENT}
  ${SESSION_REGISTRATION_FRAGMENT}
`

export const LIVE_HELP_REQUESTS_SUBSCRIPTION_FOR_POST = gql`
  subscription LiveHelpRequestsForPost($postId: Int!, $sessionRegistrationUserId: Int!) {
    live_help_request(where: { post_id: { _eq: $postId } }, order_by: { created_at: desc }) {
      ...LiveHelpRequestWithSession
    }
  }

  ${LIVE_HELP_REQUEST_WITH_SESSION_FRAGMENT}
`

export const LIVE_HELP_REQUESTS_SUBSCRIPTION_FOR_USER = gql`
  subscription LiveHelpRequestsForUser($sessionRegistrationUserId: Int!) {
    live_help_request(where: { user_id: { _eq: $sessionRegistrationUserId } }, order_by: { created_at: desc }) {
      ...LiveHelpRequestWithSession
    }
  }

  ${LIVE_HELP_REQUEST_WITH_SESSION_FRAGMENT}
`

const LIVE_HELP_REQUEST_WITH_SESSION_AND_POST_FRAGMENT = gql`
  fragment LiveHelpRequestWithSessionAndPost on live_help_request {
    ...LiveHelpRequestWithSession
    post {
      ...Post
    }
  }

  ${LIVE_HELP_REQUEST_WITH_SESSION_FRAGMENT}
  ${POST_FRAGMENT}
`

export const USER_LIVE_HELP_REQUESTS_TO_JOIN_SUBSCRIPTION = gql`
  subscription UserLiveHelpRequestsToJoin($sessionRegistrationUserId: Int!, $currentUserId: Int!) {
    live_help_request(
      where: {
        session_id: { _is_null: false }
        canceled_at: { _is_null: true }
        completed_at: { _is_null: true }
        post: { status: { _eq: "not-solved" }, user_id: { _eq: $sessionRegistrationUserId } }
      }
      order_by: { created_at: desc }
    ) {
      ...LiveHelpRequestWithSessionAndPost
    }
  }

  ${LIVE_HELP_REQUEST_WITH_SESSION_AND_POST_FRAGMENT}
`

export const UNANSWERED_LIVE_HELP_REQUESTS_COUNT_SUBSCRIPTION = gql`
  subscription UnansweredLiveHelpRequestsCount($userId: Int!, $worldIds: [Int!]) {
    live_help_request_aggregate(
      where: {
        session_id: { _is_null: true }
        canceled_at: { _is_null: true }
        completed_at: { _is_null: true }
        post: {
          status: { _eq: "not-solved" }
          user_id: { _neq: $userId }
          world_id: { _in: $worldIds }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const NOTIFICATIONS_SUBSCRIPTION = gql`
  subscription Notifications($limit: Int, $where: notification_bool_exp!) {
    notification(limit: $limit, order_by: { notify_at: desc }, where: $where) {
      ...Notification
    }
  }

  ${NOTIFICATION_FRAGMENT}
`

export const USER_NOTIFICATIONS_SUBSCRIPTION = gql`
  subscription UserNotifications($userId: Int!) {
    user_by_pk(id: $userId) {
      id
      notifications_last_viewed_at
      unread_notification_count
    }
  }
`

export const USER_UNREAD_CONVERSATIONS_SUBSCRIPTION = gql`
  subscription UserUnreadConversations($userId: Int!) {
    user_by_pk(id: $userId) {
      id
      unread_conversation_count
    }
  }
`

export const UPDATE_CONVERSATION_LAST_READ_AT_MUTATION = gql`
  mutation updateConversationLastReadAt($conversationId: Int!, $userId: Int!, $now: timestamptz!) {
    update_conversation_user(
      where: { conversation_id: { _eq: $conversationId }, user_id: { _eq: $userId } }
      _set: { last_read_at: $now }
    ) {
      affected_rows
    }
  }
`

// Begin Post Queries

export const POSTS_COUNT_SUBSCRIPTION = gql`
  subscription PostCount($where: post_bool_exp!) {
    post_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`

export const POSTS_QUERY = gql`
  query Posts($limit: Int, $offset: Int, $currentUserId: Int!, $where: post_bool_exp!) {
    post(limit: $limit, offset: $offset, order_by: { created_at: desc }, where: $where) {
      ...Post
    }
  }

  ${POST_FRAGMENT}
`

export const POST_SINGLE_QUERY = gql`
  query Post($id: Int!, $currentUserId: Int!) {
    post_by_pk(id: $id) {
      ...Post
      post_events(order_by: { created_at: asc }) {
        ...PostEvent
      }
    }
    user_by_pk(id: $currentUserId) {
      ...User
    }
  }

  ${POST_FRAGMENT}
  ${POST_EVENT_FRAGMENT}
  ${USER_FRAGMENT}
`

export const POST_UPDATES_SUBSCRIPTION = gql`
  subscription PostUpdates($id: Int!) {
    post_by_pk(id: $id) {
      id
      body_safe
      attachment_urls_safe
      # matches POST_FRAGMENT. particularly needs to include 'id' for the apollo cache
      channel {
        id
        type
        title
      }
      status
      last_edited_at
      hidden_at
      post_events_aggregate(where: { type: { _eq: "comment" } }) {
        aggregate {
          count
        }
      }
      live_help_requests(limit: 1, order_by: { created_at: desc }) {
        ...LiveHelpRequest
      }
      post_reactions(order_by: { created_at: desc }) {
        ...PostReaction
      }
    }
  }

  ${POST_REACTION_FRAGMENT}
  ${LIVE_HELP_REQUEST_FRAGMENT}
`

export const POST_EVENTS_SUBSCRIPTION = gql`
  subscription PostEvents($id: Int!) {
    post_by_pk(id: $id) {
      id
      post_events(order_by: { created_at: asc }) {
        ...PostEvent
      }
    }
  }

  ${POST_EVENT_FRAGMENT}
`

// End Post Queries
